import { WA_WINDOW } from '@ng-web-apis/common';
import { inject, Injectable } from '@angular/core';
import { filter, map, race, ReplaySubject, take, timer } from 'rxjs';
import { X_AUTH_SHOW_LOGIN } from '@core/constants';
import { AuthLoginWindow, AuthScriptState } from './auth-login.types';

const AUTH_SCRIPT_READY_TIMEOUT_MS = 5000;

@Injectable({
  providedIn: 'root',
})
export class AuthLoginService {
  private readonly window = inject(WA_WINDOW) as AuthLoginWindow;
  private readonly authScriptState$$ = new ReplaySubject<AuthScriptState>(1);
  private authScriptState: AuthScriptState = 'loading';
  private authScriptWaitInProgress = false;
  private fallbackLoginMessageSent = false;
  private loginMessageSent = false;

  /**
   * app.config вызывает это после load внешнего auth.js.
   */
  markAuthScriptReady(): void {
    this.authScriptState = 'ready';
    this.authScriptWaitInProgress = false;
    this.authScriptState$$.next('ready');
  }

  /**
   * Ошибку загрузки фиксируем отдельно, чтобы showLogin не запускал polling.
   */
  markAuthScriptFailed(): void {
    this.authScriptState = 'failed';
    this.authScriptWaitInProgress = false;
    this.authScriptState$$.next('failed');
  }

  /**
   * Открывает внешний force-reauth после готовности auth.js.
   * X_AUTH_SHOW_LOGIN грузит /logout в iframe, поэтому команда должна уйти один раз.
   */
  showLogin(): void {
    if (!this.window.X_AUTH_LOGIN_HOST) {
      return;
    }

    if (this.isAuthScriptReady()) {
      this.postShowLoginMessage();

      return;
    }

    if (this.authScriptState === 'failed') {
      this.postFallbackLoginMessageOnce('auth.js не загрузился');

      return;
    }

    if (this.authScriptWaitInProgress) {
      return;
    }

    this.authScriptWaitInProgress = true;

    race(
      this.authScriptState$$.pipe(
        filter(state => state === 'ready'),
        take(1),
        map(() => 'ready' as const),
      ),
      this.authScriptState$$.pipe(
        filter(state => state === 'failed'),
        take(1),
        map(() => 'failed' as const),
      ),
      timer(AUTH_SCRIPT_READY_TIMEOUT_MS).pipe(map(() => 'timeout' as const)),
    )
      .pipe(take(1))
      .subscribe(state => {
        this.authScriptWaitInProgress = false;

        if (state === 'ready') {
          this.postShowLoginMessage();

          return;
        }

        this.postFallbackLoginMessageOnce(
          state === 'failed'
            ? 'auth.js не загрузился'
            : 'auth.js не сообщил о готовности за 5 секунд',
        );
      });
  }

  private isAuthScriptReady(): boolean {
    return (
      this.authScriptState === 'ready' ||
      typeof this.window.xAuthUpdateSession === 'function'
    );
  }

  /**
   * Команда нужна внешнему auth.js: сам iframe авторизации создается этим скриптом.
   * Флаг не сбрасываем в рамках жизни страницы: у auth.js сейчас нет события закрытия,
   * а успешный вход присылает X_AUTH_LOGIN_SUCCESS и перезагружает текущий URL.
   */
  private postShowLoginMessage(): void {
    if (this.loginMessageSent) {
      return;
    }

    this.loginMessageSent = true;
    this.window.postMessage(X_AUTH_SHOW_LOGIN, '*');
  }

  private postFallbackLoginMessageOnce(message: string): void {
    if (this.fallbackLoginMessageSent) {
      return;
    }

    this.fallbackLoginMessageSent = true;
    console.warn(message);
    this.window.postMessage(X_AUTH_SHOW_LOGIN, '*');
  }
}
